import { compactNumber } from './formatNumbers';
import { formatBucket } from '../details/buildSegmentDetails';

/**
 * GET /audiences/{id}/summary → the summary card's stats (Figma 2341:26436). The only place that
 * knows the endpoint's field names:
 *   { audienceSize: { low, high }, matchedInfluencers: { total, macroCount, microCount },
 *     coreDemographics: { ageGroup: { bucket, weight }, gender: { label, weight } },
 *     topLocations: [{ label, city }], sharedInterests: string[] }
 * Missing pieces drop their stat instead of showing placeholders.
 *
 * @returns {{ stats: Array<{ key, label, value, hint? }>, sharedInterests: string[] } | null}
 */
const pct = (weight) => `${Math.round((Number(weight) || 0) * 100)}%`;

const locationName = (location) => location?.city || location?.label || null;

export default function normalizeAudienceSummary(data) {
  if (!data || typeof data !== 'object') return null;
  const stats = [];

  const size = data.audienceSize;
  if (size?.low != null || size?.high != null) {
    const value =
      size.low != null && size.high != null && size.low !== size.high
        ? `${compactNumber(size.low)}–${compactNumber(size.high)}`
        : compactNumber(size.high ?? size.low);
    stats.push({ key: 'reach', label: 'Reachable audience', value });
  }

  const influencers = data.matchedInfluencers;
  if (influencers?.total != null) {
    const split = [
      influencers.macroCount != null
        ? `${Number(influencers.macroCount).toLocaleString()} macro`
        : null,
      influencers.microCount != null
        ? `${Number(influencers.microCount).toLocaleString()} micro`
        : null,
    ].filter(Boolean);
    stats.push({
      key: 'influencers',
      label: 'Matched influencers',
      value: Number(influencers.total).toLocaleString(),
      hint: split.length ? split.join(' · ') : null,
    });
  }

  const { ageGroup, gender } = data.coreDemographics || {};
  if (ageGroup?.bucket || gender?.label) {
    stats.push({
      key: 'demographic',
      label: 'Core demographic',
      value: ageGroup?.bucket ? formatBucket(ageGroup.bucket) : gender.label,
      hint:
        ageGroup?.bucket && gender?.label
          ? `${pct(gender.weight)} ${String(gender.label).toLowerCase()}`
          : null,
    });
  }

  // Only the top city: a list of them got truncated in the stat tile.
  const topLocation = (Array.isArray(data.topLocations) ? data.topLocations : [])
    .map(locationName)
    .find(Boolean);
  if (topLocation) {
    stats.push({ key: 'locations', label: 'Top location', value: topLocation });
  }

  const sharedInterests = (Array.isArray(data.sharedInterests) ? data.sharedInterests : [])
    .filter((interest) => typeof interest === 'string' && interest.trim())
    .slice(0, 5);

  if (!stats.length && !sharedInterests.length) return null;
  return { stats, sharedInterests };
}
